import type { Color as PDFColor, PDFDocument, PDFFont, PDFPage } from 'pdf-lib';
import { JSX, render } from 'preact';
import type { DoodleOverlay, DoodleUser, PdfOutputOptions } from '../../../server/src/models';
import { URL_IMAGE_OVERLAY } from '../../../server/src/urls';
import { overlaySvgToG } from '../../doodle/view/DoodleOverlayImage';
import { spreadsheetToImage } from '../../doodle/view/Spreadsheet';
import { PdfTable } from '../PdfTable';
import { Timestamp } from '../components/Timestamp';
import { getDoodleFontCss, getDoodleFontTtfBuffer } from '../font';
import { notifyError } from '../notifyError';
import { createPdfDocument } from '../pdf';
import { canvasToBlob, createCanvas, defaultResponseHandler, extractPageNumbers, imageSourceToDataUrl, loadImage } from '../utils';
import { DoodleComment, DoodleDocument, DoodlePageContent } from './DoodleDocument';
import { DoodleShape } from './DoodleShape';
const overlayLayerVdom = async (doodleId: string, overlay: DoodleOverlay) => {
  const res = await fetch(URL_IMAGE_OVERLAY(doodleId, overlay.id));
  if (!res.ok) {
    await defaultResponseHandler(res, notifyError);
    throw Error('Failed to load overlay image.');
  }
  const __html = overlaySvgToG(await res.text());
  // LINK / VAULT - MARKUP 連携 検証 No.10, デプロイ後検証 No.4: LINK / VAULT 図面は縦横半分表示なので重ねる図形も縦横半分にします。
  // eslint-disable-next-line react/no-danger
  return <g key={overlay.layerId} transform="scale(0.5)" dangerouslySetInnerHTML={{
    __html
  }} />;
};
const doodleShapesToImage = async (doodleId: string, {
  layers,
  overlays,
  shapes,
  width,
  height
}: DoodlePageContent, additionalDom?: JSX.Element) => {
  const [css, vdom] = await Promise.all([getDoodleFontCss(), Promise.all(layers.values().filter(layer => layer.visible).map(async layer => {
    if (layer.type === 'overlay') {
      const overlay = overlays?.find(o => o.layerId === layer.id);
      return overlay && overlayLayerVdom(doodleId, overlay);
    } else {
      const vdom$s = shapes.getShapesByLayerId(layer.id)?.values().map(async shape => shape.type === 'image' ? shape.with({
        src: await imageSourceToDataUrl(shape.properties.src)
      }).vdom : shape.vdom).toArray();
      return vdom$s && <g key={layer.id} data-layer-id={layer.id} children={await Promise.all(vdom$s)} />;
    }
  }))]);
  additionalDom && vdom.push(additionalDom);
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  render(vdom, svg);
  return loadImage(`data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" class="font-doodle"><style>${css}</style>${encodeURIComponent(svg.innerHTML)}</svg>`);
};
export const doodlePageContentToCanvas = async (doodleId: string, content: DoodlePageContent, timestamp: number, finalized: unknown, resolutionScale: number) => {
  const {
    width,
    height
  } = content;
  const isVisibleDrawing = content.layers.find(layer => layer.type === 'drawing')?.visible ?? true;
  const [background, shapesImage] = await Promise.all([isVisibleDrawing ? content.render(true) : undefined, doodleShapesToImage(doodleId, content, <Timestamp timestamp={timestamp} finalized={finalized} height={height} />)]);
  const canvas = createCanvas({
    width: width * resolutionScale,
    height: height * resolutionScale
  });
  const ctx = canvas.getContext('2d')!;
  background && ctx.drawImage(background, 0, 0, canvas.width, canvas.height);
  ctx.drawImage(shapesImage, 0, 0, canvas.width, canvas.height);
  return canvas;
};
const pdfRgb = (r: number, g: number, b: number) => ({
  type: 'RGB',
  red: r / 255,
  green: g / 255,
  blue: b / 255
}) as PDFColor;
const drawCommentNumber = (page: PDFPage, shape: DoodleShape, commentNumber: string, {
  font
}: {
  font: PDFFont;
}) => {
  const {
    x
  } = shape.p1;
  const y = page.getHeight() - shape.p1.y;
  page.drawCircle({
    x,
    y,
    size: 10,
    color: pdfRgb(255, 243, 0),
    borderColor: pdfRgb(204, 194, 0),
    borderWidth: 1,
    opacity: 0.75
  });
  page.drawText(commentNumber, {
    x: x - (commentNumber.length === 1 ? 3 : 6),
    y: y - 5,
    size: 12,
    lineHeight: 12,
    color: pdfRgb(32, 32, 32),
    font
  });
};
const addCommentPages = (doc: PDFDocument, comments: readonly {
  readonly pageNumber: number;
  readonly shapeNumber: string;
  readonly comment: DoodleComment;
}[], users: readonly DoodleUser[], {
  font
}: {
  font: PDFFont;
}) => {
  const toDateTimeString = (timestamp: number) => {
    const d = new Date(timestamp);
    const pad2 = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}  ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  };
  const uidToUserName = (uid: string) => {
    const user = users.find(user => user.uid === uid);
    return user?.name || user?.email || '';
  };
  const fontSize = 10;
  const commentTable = new PdfTable({
    pageFactory: () => {
      const commentPage = doc.addPage([595.28, 841.89]); // PageSizes.A4
      commentPage.setFont(font);
      commentPage.setFontSize(fontSize);
      commentPage.setLineHeight(12);
      return commentPage;
    },
    columns: [{
      header: {
        text: 'ページ'
      },
      width: font.widthOfTextAtSize('ページ', fontSize),
      textAlign: 'right'
    }, {
      header: {
        text: '番号'
      },
      width: font.widthOfTextAtSize('番号', fontSize),
      textAlign: 'right'
    }, {
      header: {
        text: '日時'
      },
      width: font.widthOfTextAtSize(toDateTimeString(new Date('2088-08-08T08:08').getTime()), fontSize)
    }, {
      header: {
        text: 'ユーザー'
      },
      width: 96
    }, {
      header: {
        text: 'コメント'
      },
      width: -1
    }],
    margin: {
      x: 32,
      y: 32
    },
    gap: {
      row: 4,
      column: 16
    }
  });
  let previousPageNumber = NaN;
  for (const {
    pageNumber,
    shapeNumber,
    comment
  } of comments) {
    const p = pageNumber === previousPageNumber ? '' : String(pageNumber);
    commentTable.appendRow([p, shapeNumber, toDateTimeString(comment.createdAt), uidToUserName(comment.uid), comment.comment]);
    for (const reply of comment.replies.values()) {
      commentTable.appendRow(['', '', toDateTimeString(reply.createdAt), uidToUserName(reply.uid), reply.comment]);
    }
    previousPageNumber = pageNumber;
  }
};
export type OnDownloadProgress = (state: 'pending' | 'progress' | 'finalizing' | 'complete', pageNumber: number, totalPageCount: number) => void;
export const addPdfPage = async (doodleId: string, pdf: PDFDocument, content: DoodlePageContent, timestamp: number, finalizedAt: number | undefined, options?: PdfOutputOptions) => {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const resolutionScale = options?.resolutionScale || content.resolutionScale;
  const canvas = await doodlePageContentToCanvas(doodleId, content, timestamp, finalizedAt, resolutionScale);
  const canvasBlob = await canvasToBlob(canvas, 'image/png');
  canvas.width = canvas.height = 0;
  const embeddedPng = await pdf.embedPng(await canvasBlob.arrayBuffer());
  const {
    width,
    height
  } = content;
  const drawingPage = pdf.addPage([width, height]);
  drawingPage.drawImage(embeddedPng, {
    width,
    height
  });
  return drawingPage;
};
export const doodleDocumentToImagePdf = async (doodleId: string, doc: DoodleDocument, users: readonly DoodleUser[], timestamp: number, finalizedAt: number | undefined, options?: PdfOutputOptions, onDownloadProgress?: OnDownloadProgress): Promise<Uint8Array<ArrayBuffer>> => {
  const outputsCommentList = !!options?.comments;
  const pageNumbers = extractPageNumbers(options?.pageNumbers, doc.totalPageCount);
  onDownloadProgress?.('pending', 0, pageNumbers.length);
  const imagePdf = await createPdfDocument({
    fontkit: outputsCommentList
  });
  const font = outputsCommentList ? await imagePdf.embedFont(await getDoodleFontTtfBuffer(), {
    subset: true
  }) : undefined;
  const comments: {
    pageNumber: number;
    shapeNumber: string;
    comment: DoodleComment;
  }[] = [];
  let shapeNumber = 0;
  for (let i = 0; i < pageNumbers.length; i++) {
    onDownloadProgress?.('progress', i, pageNumbers.length);
    const pageNumber = pageNumbers[i];
    const content = doc.getPage(pageNumber);
    const drawingPage = await addPdfPage(doodleId, imagePdf, content, timestamp, finalizedAt, options);
    if (outputsCommentList) {
      const shapeIdCommentsMap = Map.groupBy(content.comments.values(), comment => comment.shapeId);
      shapeIdCommentsMap.keys().map(shapeId => content.shapes.findShape(shapeId)).filter(shape => !!shape).filter(({
        properties: {
          layerId
        }
      }) => content.layers.find(layer => layer.id === layerId)?.visible).toArray().sort((a, b) => a.p1.y - b.p1.y || a.p1.x - b.p1.x).forEach(shape => {
        const shapeNumberString = String(++shapeNumber);
        drawCommentNumber(drawingPage, shape, shapeNumberString, {
          font: font!
        });
        for (const comment of shapeIdCommentsMap.get(shape.properties.id)!) {
          comments.push({
            pageNumber: i + 1,
            shapeNumber: shapeNumberString,
            comment
          });
        }
      });
    }
  }
  outputsCommentList && addCommentPages(imagePdf, comments, users, {
    font: font!
  });
  if (options?.spreadsheet) {
    for (const spreadsheetImage of spreadsheetToImage()) {
      const spreadsheetPage = imagePdf.addPage();
      const embeddedPng = await imagePdf.embedPng(spreadsheetImage);
      const height = embeddedPng.height * 0.25;
      spreadsheetPage.drawImage(embeddedPng, {
        y: spreadsheetPage.getHeight() - height,
        width: embeddedPng.width * 0.25,
        height
      });
    }
  }
  onDownloadProgress?.('finalizing', pageNumbers.length, pageNumbers.length);
  await new Promise(resolve => setTimeout(resolve, 32));
  // pdf-lib は更新が止まっており、実際 SharedArrayBuffer ではなく ArrayBuffer のはずですが、
  // 型パラメーターが明示されておらず ArrayBufferLike 扱いになり型エラーになるので型アサーションします。
  const saved = (await imagePdf.save()) as Uint8Array<ArrayBuffer>;
  onDownloadProgress?.('complete', pageNumbers.length, pageNumbers.length);
  return saved;
};